import { useScienceTutorSection } from './scienceTutorContext';
import { Scale, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import {
  checkPrimaryStandard,
  markVolumetricAnswer,
  solveStandardProblem,
  type VolumetricCheck,
} from './volumetricAnalysis';
import { PRIMARY_STANDARD_CASES, STANDARD_PROBLEMS } from './volumetricAnalysisData';
import { AcidBaseFeedback } from './AcidBaseCalculationsVisuals';
import { MoleMap } from './VolumetricAnalysisVisuals';

const CHOICE_BUTTON =
  'rounded-full border px-4 py-2 text-sm font-extrabold transition-colors motion-reduce:transition-none';

function PrimaryStandardSorter() {
  const [caseId, setCaseId] = useState(PRIMARY_STANDARD_CASES[0].id);
  useScienceTutorSection('PrimaryStandardSorter', { caseId });
  const [check, setCheck] = useState<VolumetricCheck | null>(null);
  const item =
    PRIMARY_STANDARD_CASES.find((candidate) => candidate.id === caseId) ??
    PRIMARY_STANDARD_CASES[0];

  return (
    <section
      aria-label="Primary standard check"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-wash-mint text-ink">
          <ShieldCheck className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Predict before you weigh
          </p>
          <h2 className="text-xl font-extrabold text-ink">Primary standard or not?</h2>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium leading-6 text-slate2">
        A primary standard can be weighed straight into a standard solution of exactly known
        concentration. Anything else must be standardised by titration first.
      </p>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Choose a substance">
        {PRIMARY_STANDARD_CASES.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === item.id}
            onClick={() => {
              setCaseId(candidate.id);
              setCheck(null);
            }}
            className={`${CHOICE_BUTTON} ${
              candidate.id === item.id
                ? 'border-brand-sky bg-wash-sky text-ink'
                : 'border-hairline bg-white text-slate2'
            }`}
          >
            {candidate.formula}
            <span className="sr-only"> — {candidate.name}</span>
          </button>
        ))}
      </div>
      <p className="mt-5 text-lg font-extrabold text-ink">
        {item.name} <span className="text-slate2">({item.formula})</span>
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCheck(checkPrimaryStandard(item, true))}
          className={`${CHOICE_BUTTON} border-ink bg-white text-ink`}
        >
          Primary standard
        </button>
        <button
          type="button"
          onClick={() => setCheck(checkPrimaryStandard(item, false))}
          className={`${CHOICE_BUTTON} border-ink bg-white text-ink`}
        >
          Must be standardised
        </button>
      </div>
      {check ? (
        <div className="mt-4" role="region" aria-label="Primary standard feedback">
          <AcidBaseFeedback check={check} working={[]} />
        </div>
      ) : null}
    </section>
  );
}

function StandardSolutionProblems() {
  const [problemId, setProblemId] = useState(STANDARD_PROBLEMS[0].id);
  const [answer, setAnswer] = useState('');
  useScienceTutorSection('StandardSolutionProblems', { problemId, answer });
  const [check, setCheck] = useState<VolumetricCheck | null>(null);
  const problem =
    STANDARD_PROBLEMS.find((candidate) => candidate.id === problemId) ?? STANDARD_PROBLEMS[0];
  const solved = solveStandardProblem(problem);
  const model = solved.value;
  const revealed = check !== null && check.status !== 'invalid';

  return (
    <section
      aria-label="Standard solution problems"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-wash-sunshine text-ink">
          <Scale className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Make a standard solution
          </p>
          <h2 className="text-xl font-extrabold text-ink">
            {STANDARD_PROBLEMS.length} reviewed problems
          </h2>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Choose a problem">
        {STANDARD_PROBLEMS.map((candidate, index) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === problem.id}
            onClick={() => {
              setProblemId(candidate.id);
              setAnswer('');
              setCheck(null);
            }}
            title={candidate.title}
            className={`rounded-full border px-3 py-1.5 text-sm font-extrabold ${
              candidate.id === problem.id
                ? 'border-brand-sky bg-wash-sky text-ink'
                : 'border-hairline bg-white text-slate2 hover:border-brand-sky/40'
            }`}
          >
            {index + 1}
            <span className="sr-only">. {candidate.title}</span>
          </button>
        ))}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-3">
          <h3 className="text-lg font-extrabold text-ink">{problem.title}</h3>
          <p className="font-medium leading-7 text-ink-soft">{problem.context}</p>
          {model ? (
            <>
              <label htmlFor="standard-answer" className="block text-sm font-extrabold text-ink">
                Your answer for {model.quantityLabel} ({model.unit})
              </label>
              <div className="flex gap-2">
                <input
                  id="standard-answer"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={answer}
                  onChange={(event) => {
                    setAnswer(event.target.value);
                    setCheck(null);
                  }}
                  className="min-w-0 flex-1 rounded-xl border border-hairline bg-surface px-4 py-3 font-bold text-ink focus:border-brand-sky focus:outline-none focus:ring-4 focus:ring-brand-sky/15"
                />
                <span className="grid place-items-center rounded-xl bg-surface px-3 text-sm font-extrabold text-slate2">
                  {model.unit}
                </span>
              </div>
              <button
                type="button"
                disabled={!answer.trim()}
                onClick={() => setCheck(markVolumetricAnswer(solved, answer))}
                className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Check answer
              </button>
              {check ? (
                <div role="region" aria-label="Standard solution feedback">
                  <AcidBaseFeedback check={check} working={model.working} />
                </div>
              ) : null}
            </>
          ) : (
            <p role="alert" className="rounded-2xl bg-wash-sunshine p-4 text-sm font-bold text-ink">
              {solved.error}
            </p>
          )}
        </div>
        <div className="min-w-0 rounded-[1.5rem] border border-hairline bg-surface p-4 sm:p-5">
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.14em] text-slate2">
            Mole map
          </p>
          {revealed && model ? (
            <MoleMap steps={model.flow} />
          ) : (
            <p className="rounded-2xl border-2 border-dashed border-hairline px-4 py-6 text-center text-sm font-bold text-slate2">
              Check an answer to reveal the mole map.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export function VolumetricAnalysisStandard() {
  return (
    <div className="space-y-6">
      <PrimaryStandardSorter />
      <StandardSolutionProblems />
    </div>
  );
}
